import React, { useEffect, useRef, useState } from 'react'
import { MenuProps } from 'components/menu'
import FloatingMenu from 'components/menu/floating'
import { MenuHeaderAction, MenuItem, MenuList, MenuSearch } from 'components/menu/items'
import { PanelAction, PanelFooter } from 'components/menu/panel'
import Toggle from 'components/toggle'
import { Invisible, Visible } from 'components/icons'
import { matchesAllTokens } from 'helpers/search'
import { IColumnTypes } from 'components/spreadsheet/types'
import IconSelector from 'components/spreadsheet/components/cell/components/icon'

// Which of the view's columns show: a search, then each column as a menu row with its type's icon,
// its name and a switch (on while it shows), and Hide all / Show all (of those the search finds) at
// the foot. Reset, in the header where other panels have Clear all, shows every column again. A
// column the view can't do without (grouped by, say) is listed with its switch off-limits and the
// reason as a tooltip.

interface HideMenuProps extends MenuProps {
  columns: Array<{ publicId: string; name: string; kind: IColumnTypes }>
  hiddenColumnIds: string[]
  /** Why a column can't be hidden, or undefined when it can. */
  cantHideReason: (columnId: string) => string | undefined
  onHide: (columnId: string) => void
  onShow: (columnId: string) => void
}

const HideMenu: React.FC<HideMenuProps> = ({
  id,
  menuState,
  setMenuState,
  width,
  columns,
  hiddenColumnIds,
  cantHideReason,
  onHide,
  onShow
}) => {
  const [search, setSearch] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  // Ready to type a column's name as soon as it opens, as the options lists are.
  useEffect(() => searchRef.current?.focus(), [])
  const shown = columns.filter((column) => matchesAllTokens({ label: column.name, value: column.publicId }, search))
  const changeable = shown.filter((column) => !cantHideReason(column.publicId))
  const hiddenOf = (columnId: string) => hiddenColumnIds.includes(columnId)
  // Every hidden column, from the view's own list rather than the rows here, which leave out
  // required columns (one can be hidden before it's made required), and even one that couldn't be
  // hidden now (grouped by since, say): those reasons are all about hiding, and nothing keeps a
  // column from showing again. A deleted column leaves the list, so none of these is stale.
  const hidden = hiddenColumnIds

  return (
    <FloatingMenu
      id={id}
      menuState={menuState}
      setMenuState={setMenuState}
      width={width}
      zIndex={3000}
      title="Hide columns"
      action={
        hidden.length > 0 && (
          <MenuHeaderAction onClick={() => hidden.forEach((columnId) => onShow(columnId))}>Reset</MenuHeaderAction>
        )
      }
    >
      <MenuSearch
        ref={searchRef}
        placeholder="Search columns"
        aria-label="Search columns"
        onChange={(event) => setSearch(event.target.value)}
      />
      <MenuList className="max-h-[320px] overflow-y-auto">
        {shown.length === 0 && <div className="px-2 py-1.5 text-[13px] text-muted select-none">No columns found</div>}
        {shown.map((column) => {
          const reason = cantHideReason(column.publicId)
          const isShown = !hiddenOf(column.publicId)
          const flip = (show: boolean) => (show ? onShow(column.publicId) : onHide(column.publicId))
          return (
            <MenuItem
              key={column.publicId}
              data-testid="hide-column-row"
              icon={<IconSelector kind={column.kind} />}
              disabledReason={reason}
              onClick={() => flip(!isShown)}
              end={<Toggle aria-label={`Show ${column.name}`} toggled={isShown} onToggle={flip} disabled={!!reason} />}
            >
              {column.name}
            </MenuItem>
          )
        })}
      </MenuList>
      <PanelFooter>
        {/* All of those the search shows, as a list's all-at-once buttons go. */}
        <PanelAction
          icon={<Invisible />}
          onClick={() => changeable.filter((column) => !hiddenOf(column.publicId)).forEach((c) => onHide(c.publicId))}
        >
          Hide all
        </PanelAction>
        <PanelAction
          icon={<Visible />}
          onClick={() => changeable.filter((column) => hiddenOf(column.publicId)).forEach((c) => onShow(c.publicId))}
        >
          Show all
        </PanelAction>
      </PanelFooter>
    </FloatingMenu>
  )
}

export default HideMenu
